import { getAllListItems } from '@cineamo/ui/src/helpers/get-all-list-items';

import { roomGeometryFromDto } from '@cineamo/ui/src/seat-map/room-geometry-from-dto';
import { CinemaRoom, RoomObject, Seat, SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';

import { CinemaRoomDto } from '../../models/cinema-room/CinemaRoomDto.types';
import { RoomObjectDto } from '../../models/room-object/RoomObjectDto.types';
import { SeatDto } from '../../models/seat/SeatDto.types';
import { SeatCategoryDto } from '../../models/seat-category/SeatCategoryDto.types';
import { ShowingDto } from '../../models/showing/ShowingDto.types';
import { getRoomObjects } from '../../resources/room-object/room-object-api';
import { RoomObjectApiEmbed } from '../../resources/room-object/room-object-api.embed';
import { getCinemaRoomById } from '../cinema-room/cinema-room-api';
import { getSeats } from '../seat/seat-api';
import { SeatApiEmbed } from '../seat/seat-api.embed';
import { getSeatCategories } from '../seat-category/seat-category-api';

export class SeatMapApi {
    async loadRoom(id: ShowingDto['cinemaRoomId']): Promise<CinemaRoom | null> {
        const room = await getCinemaRoomById(id);
        return room ? this.roomFromDto(room) : null;
    }

    async loadRoomObjects(roomId: ShowingDto['cinemaRoomId']): Promise<RoomObject[]> {
        const roomObjects = await getAllListItems({
            queryFn: (page: number) =>
                getRoomObjects({
                    cinemaRoomId: roomId,
                    embed: [RoomObjectApiEmbed.Shape, RoomObjectApiEmbed.Transform],
                    per_page: 100,
                    page: page
                }),
            listAccessor: 'room-objects'
        });

        return roomObjects.map((roomObject) => this.roomObjectFromDto(roomObject)) ?? null;
    }

    async loadSeats(roomId: ShowingDto['cinemaRoomId']): Promise<Seat[]> {
        const seats = await getAllListItems({
            queryFn: (page: number) =>
                getSeats({
                    cinemaRoomId: roomId,
                    embed: [SeatApiEmbed.Shape, SeatApiEmbed.Transform],
                    page: page,
                    per_page: 100
                }),
            listAccessor: 'seats'
        });

        return seats.map((seat) => this.seatFromDto(seat)) ?? null;
    }

    async loadSeatCategories(cinemaId: ShowingDto['cinemaId']): Promise<SeatCategory[]> {
        const categories = await getAllListItems({
            queryFn: (page: number) => getSeatCategories({ cinemaId, page }),
            listAccessor: 'seat-categories'
        });

        return categories.map((category) => this.seatCategoryFromDto(category)) ?? null;
    }

    public roomFromDto(roomDto: CinemaRoomDto): CinemaRoom {
        const cinemaRoomShape = roomDto._embedded?.cinemaRoomShape;

        return {
            id: roomDto.id,
            cinemaId: roomDto.cinemaId,
            name: roomDto.name,
            basePrice: roomDto.basePrice,
            width: cinemaRoomShape?.width ?? 15000,
            depth: cinemaRoomShape?.depth ?? 20000,
            height: cinemaRoomShape?.height ?? 5000,
            geometry: roomGeometryFromDto(cinemaRoomShape?.geometry),
            dto: roomDto
        };
    }

    public roomObjectFromDto(roomObjectDto: RoomObjectDto): RoomObject {
        const roomObjectShape = roomObjectDto._embedded?.shape;

        return {
            id: roomObjectDto.id,
            name: roomObjectDto.name,
            width: roomObjectShape?.width ?? 1000,
            depth: roomObjectShape?.depth ?? 1000,
            height: roomObjectShape?.height ?? 1000,
            type: roomObjectDto.type ?? null,
            dto: roomObjectDto
        };
    }

    private seatFromDto(seatDto: SeatDto): Seat {
        const seatShape = seatDto._embedded?.seatShape;
        const seatTransform = seatDto._embedded?.seatTransform;
        const seatCategory = seatDto._embedded?.seatCategory;
        const seatPosition = seatDto._embedded?.seatPosition;

        const height = seatShape?.height ?? 1000;

        return {
            id: seatDto.id,
            width: seatShape?.width ?? 1000,
            type: seatDto.type,
            height,
            depth: seatShape?.depth ?? 1000,
            position: {
                x: seatTransform?.positionX ?? 0,
                y: seatTransform?.positionY ?? -height,
                z: seatTransform?.positionZ ?? 0
            },
            rotation: {
                x: seatTransform?.rotationX ?? 0,
                y: seatTransform?.rotationY ?? 0,
                z: seatTransform?.rotationZ ?? 0
            },
            category: seatCategory ? this.seatCategoryFromDto(seatCategory) : undefined,
            rowName: seatPosition?._embedded?.seatRow?.name ?? '',
            positionName: seatPosition?.name ?? 0,
            isBestSeat: seatDto.isBestSeat === true,
            isDbox: seatDto.isDbox === true,
            look: seatDto.look ?? null
        };
    }

    private seatCategoryFromDto(seatCategoryDto: SeatCategoryDto): SeatCategory {
        return {
            id: seatCategoryDto.id,
            name: seatCategoryDto.name,
            priceChange: seatCategoryDto.priceChange,
            color: seatCategoryDto.color,
            imageUrl: seatCategoryDto.imageUrl,
            description: seatCategoryDto.description,
            benefits: seatCategoryDto.benefits
        };
    }
}
